import { Card, Collapse, FormLabel, Grid, MenuItem } from "@mui/material";
import React, { useMemo, useRef, useState } from "react";
import CardBody from "../../../../components/Card/CardBody";
import FormSelectTC from "../../../../components/Forms/FormSelectTC";
import FormTextTC from "../../../../components/Forms/FormTextTC";
import FormAsyncTC from "../../../../components/Forms/FormAsyncTC";
import FormSwitchTC from "../../../../components/Forms/FormSwitchTC";
import FormFieldTwoColumns from "../../../../components/Forms/Layouts/FormFieldTwoColumns";
import TranslationWrapper, { getTranslation } from "../../../../components/TranslationWrapper/TranslationWrapper.tsx";
import { convertObjectToSelectOptions } from "../../../../templateHelpers";
import MenuSyncSettings from "../MenuSyncSettings";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { Alert } from "@mui/lab";
import Buttonbar from "../LightspeedK/Components/Buttonbar";
import Overzicht from "../LightspeedK/Components/Overzicht";
import FormJson from "../../../../components/Forms/FormJson";
import Method from "../LightspeedK/InputForms/Method";
import Accounts from "../LightspeedK/InputForms/Accounts";
import Headers from "../LightspeedK/InputForms/Headers";

//defining Untill guest notifier constants
const UNTILL_GUEST_NOITIFIER_OFF = 0;
const UNTILL_GUEST_NOITIFIER_ON = 1;
const UNTILL_GUEST_NOITIFIER_FOOD = 2;
const UNTILL_GUEST_NOITIFIER_DRINKS = 3;

function Untill() {
  const vestigingId = useWatch({ name: "id" });
  const untillOrderTypes = [
    {
      value: "table",
      label: <TranslationWrapper tDefault={"Op Tafel"} tKey={"vestigingen.form.untill.ordermode.table"} />,
    },
    {
      value: "order",
      label: <TranslationWrapper tDefault={"Orders"} tKey={"vestigingen.form.untill.ordermode.order"} />,
    },
  ];
  const untillDatabaseName = useWatch({ name: "custom_data.untill_database_name" });
  const untillSslEnabled = useWatch({ name: "custom_data.untill_ssl_enabled" });
  const useOptionGroups = useWatch({ name: "extra_settings__use_option_groups" });

  const untillApiTypes = [
    {
      value: 0,
      label: <TranslationWrapper tDefault={"SOAP"} tKey={"vestigingen.form.untill.api.type.soap"} />,
    },
    {
      value: 1,
      disabled: !untillDatabaseName || !useOptionGroups,
      label: <TranslationWrapper tDefault={"REST"} tKey={"vestigingen.form.untill.api.type.rest"} />,
    },
  ];

  const customData = useWatch({ name: "custom_data" });

  const untillCourseOptions = useMemo(() => {
    const untillCoursesArray = customData?.untill_courses ?? [];

    // Default value to be added at the start of the array
    const defaultValue = {
      value: false,
      label: getTranslation("custom_data.untill.untill_courses", "none"),
    };

    return [
      defaultValue,
      ...untillCoursesArray.map((item) => ({
        value: item.id,
        label: `${item.name} (${item.id})`,
      })),
    ];
  }, [customData]);

  const untillCourseOptionsWithoutDefault = useMemo(() => {
    return untillCourseOptions.slice(1);
  }, [untillCourseOptions]);

  const untillRoomserviceModeEnabled = useWatch({ name: "custom_data.untill_roomservice_mode" });

  const untillOrderModes = convertObjectToSelectOptions(untillOrderTypes);
  const untillApiTypeOptions = convertObjectToSelectOptions(untillApiTypes);

  const untillGuestNotifierTypes = [
    {
      value: UNTILL_GUEST_NOITIFIER_OFF,
      label: <TranslationWrapper tDefault={"OFF (Never)"} tKey={"vestigingen.form.untill.guest_notifier_off"} />,
    },
    {
      value: UNTILL_GUEST_NOITIFIER_ON,
      label: <TranslationWrapper tDefault={"ON (Always)"} tKey={"vestigingen.form.untill.guest_notifier_on"} />,
    },
    {
      value: UNTILL_GUEST_NOITIFIER_FOOD,
      label: (
        <TranslationWrapper
          tDefault={"FOOD (Only when order contains Food)"}
          tKey={"vestigingen.form.untill.guest_notifier_food"}
        />
      ),
    },
    {
      value: UNTILL_GUEST_NOITIFIER_DRINKS,
      label: (
        <TranslationWrapper
          tDefault={"DRINKS (Only when order contains Drinks)"}
          tKey={"vestigingen.form.untill.guest_notifier_drinks"}
        />
      ),
    },
  ];
  const untillGuestNotifierModes = convertObjectToSelectOptions(untillGuestNotifierTypes);

  const { menukaartVestigingId } = useFormContext();
  const untillRestIpAndPort = useWatch({ name: "untillServerSecondary" });
  const customDataUntillRest = useWatch({ name: "custom_data.untill_rest" });

  const isUsingSoap = useMemo(() => {
    return customDataUntillRest != 1;
  }, [customDataUntillRest]);

  const restIsReady = useMemo(() => {
    return Boolean(untillRestIpAndPort && untillDatabaseName && !isUsingSoap);
  }, [untillRestIpAndPort, untillDatabaseName, isUsingSoap]);

  /**
   * Payment IDS
   */
  const getInputForm = (type, value = undefined) => {
    let form = null;
    let action = "ADD";
    if (value !== undefined) {
      action = "EDIT";
    }

    switch (type) {
      case "method":
        form = (
          <Method
            closeForm={handleCloseClick}
            methods={extra_settings__paymentApiIdsValue ? JSON.parse(extra_settings__paymentApiIdsValue) : []}
            onSave={(value) => {
              const arr = extra_settings__paymentApiIdsValue ? JSON.parse(extra_settings__paymentApiIdsValue) : [];
              if (action === "ADD") {
                arr.push(value);
              } else if (action === "EDIT") {
                arr.forEach((obj, key) => {
                  if (obj.name === value.oldName) {
                    delete value.oldName;
                    arr[key] = value;
                  }
                });
              }
              extra_settings__paymentApiIdsOnChange(JSON.stringify(arr));
            }}
            salesareaId={vestigingId}
            type={"UNTILL"}
            value={value}
          />
        );
        break;
    }

    return form;
  };

  const {
    field: { value: extra_settings__paymentApiIdsValue, onChange: extra_settings__paymentApiIdsOnChange },
  } = useController({ name: "extra_settings__paymentApiIds" });
  const form = useRef(null);
  const [showInputForm, setShowInputForm] = useState(false);
  const handleButtonClick = (type) => {
    form.current = getInputForm(type);
    setShowInputForm(true);
  };

  const handleCloseClick = () => {
    form.current = null;
    setShowInputForm(false);
  };

  const onDelete = (objectToDelete) => {
    extra_settings__paymentApiIdsOnChange(
      JSON.stringify(JSON.parse(extra_settings__paymentApiIdsValue).filter((obj) => obj.name !== objectToDelete.name))
    );
  };

  return (
    <Card>
      <CardBody alignItems={"flex-start"} style={{ padding: 16, display: "flex", flexWrap: "wrap" }}>
        <Grid container spacing={1}>
          <Grid alignContent={"flex-start"} container item lg={6} xs={12}>
            <Grid item xs={12}>
              <h3>
                <TranslationWrapper tDefault={"Untill"} tKey={"vestigingen.form.untill.headers.untill"} />
              </h3>
            </Grid>
            <FormSelectTC
              name={"untillOrderMode"}
              options={untillOrderModes}
              tDefault={"Untill order mode"}
              tKey={"vestigingen.form.bork.label.untillOrderMode"}
            />
            <FormTextTC name={"untilluser"} tDefault={"Gebruiker"} tKey={"vestigingen.form.untill.label.untilluser"} />
            <FormTextTC name={"untillpass"} tDefault={"Wachtwoord"} tKey={"vestigingen.form.untill.label.untillpass"} />
            <FormTextTC name={"untillserver"} tDefault={"Server"} tKey={"vestigingen.form.untill.label.untillserver"} />
            <FormTextTC
              name={"untillServerSecondary"}
              tDefault={"REST Server IP + Port (Usually 3064)"}
              tKey={"vestigingen.form.untill.label.rest_server"}
            />
            <FormSwitchTC
              name={"custom_data.untill_ssl_enabled"}
              tDefault={"Server has SSL enabled"}
              tKey={"vestigingen.form.untill.label.use_sll"}
            />
            {untillSslEnabled ? (
              <FormSwitchTC
                defaultValue={true}
                name={"custom_data.untill_verify_ssl"}
                tDefault={"Verify SSL certificate"}
                tKey={"vestigingen.form.untill.label.use_sll"}
              />
            ) : null}

            {!untillDatabaseName || !useOptionGroups ? (
              <Alert severity={"info"}>
                {getTranslation(
                  "untill.rest.option_group.alert",
                  "REST Api requires the Untill database name to be set and option groups to be enabled within the sync configuration."
                )}
              </Alert>
            ) : (
              ""
            )}

            <Alert severity={"warning"}>Please sync after swapping between REST and SOAP</Alert>
            <FormSelectTC
              defaultValue={0}
              name={"custom_data.untill_rest"}
              options={untillApiTypeOptions}
              tDefault={"Api type"}
              tKey={"vestigingen.form.until.api.type"}
            />
            <FormTextTC
              name={"custom_data.untill_database_name"}
              tDefault={"Untill database name"}
              tKey={"vestigingen.form.untill.label.untill_database_name"}
            />
            <FormAsyncTC
              async={{
                url:
                  "core/producten/search?fields=id as value, naam as label&per_page=50&sort_field=naam&vestiging_id=" +
                  menukaartVestigingId,
                searchField: "naam",
              }}
              key={"apiaccounts__serviceProduct_id" + menukaartVestigingId}
              name={"apiaccounts__serviceProduct_id"}
              nullable={true}
              tDefault={"Bediening (service)"}
              tKey={"vestigingen.form.apiaccounts.label.serviceProduct_id"}
            />
            <FormAsyncTC
              async={{
                url:
                  "core/producten/search?fields=id as value, naam as label&per_page=50&sort_field=naam&vestiging_id=" +
                  menukaartVestigingId,
                searchField: "naam",
              }}
              key={"apiaccounts__billProduct_id" + menukaartVestigingId}
              name={"apiaccounts__billProduct_id"}
              nullable={true}
              tDefault={"Rekening (service)"}
              tKey={"vestigingen.form.apiaccounts.label.billProduct_id"}
            />
            <FormTextTC
              name={"untillSalesArea"}
              tDefault={"Sales Area"}
              tKey={"vestigingen.form.untill.label.untillSalesArea"}
            />
            <Collapse in={restIsReady} sx={{ width: 1 }} unmountOnExit>
              <FormAsyncTC
                async={{ url: `core/pos-api/untill/getSalesAreas?vid=${vestigingId}` }}
                name={"untillSalesArea"}
                tDefault={"Sales Area"}
                tKey={"vestigingen.form.untill.label.untillSalesArea"}
              />
            </Collapse>
            <FormSwitchTC
              name={"untillCloseTable"}
              tDefault={"Close table"}
              tKey={"vestigingen.form.untill.label.untillCloseTable"}
            />
            <FormSwitchTC
              name={"skipPaymentToPos"}
              tDefault={"No payment call to POS"}
              tKey={"vestigingen.form.untill.label.skipPaymentToPos"}
            />
            <FormSwitchTC
              name={"untillAutoSync"}
              tDefault={"Auto sync state"}
              tKey={"vestigingen.form.untill.label.untillAutoSync"}
            />
            <FormTextTC
              name={"extra_settings__tableNamePrefix"}
              tDefault={"Table Name Prefix (%=space)"}
              tKey={"vestigingen.form.untill.label.tableNamePrefix"}
            />
            <FormSwitchTC
              name={"extra_settings__untillTableRoulation"}
              tDefault={"Table roulette"}
              tKey={"vestigingen.form.untill.label.tableRoulette"}
            />
            <FormSelectTC
              name={"extra_settings__untillSendoCntAsGuestNotifier"}
              options={untillGuestNotifierModes}
              tDefault={"Send order counter as guest notifier code"}
              tKey={"vestigingen.form.untill.label.untillSendoCntAsGuestNotifier"}
            />
            <FormTextTC
              name={"untillPaymentId"}
              tDefault={"PaymentID"}
              tKey={"vestigingen.form.untill.label.untillPaymentId"}
            />
            <Collapse in={restIsReady} sx={{ width: 1 }} unmountOnExit>
              <FormAsyncTC
                async={{ url: `core/pos-api/untill/getPayments?vid=${vestigingId}` }}
                name={"untillPaymentId"}
                tDefault={"PaymentID"}
                tKey={"vestigingen.form.untill.label.untillPaymentId"}
              />
            </Collapse>
            <FormTextTC
              name={"untillPrinterID"}
              tDefault={"PrinterID"}
              tKey={"vestigingen.form.untill.label.untillPrinterID"}
            />
            <FormSwitchTC
              name={"custom_data.untill_roomservice_mode"}
              tDefault={"Roomservice mode"}
              tKey={"custom_data.untill_roomservice_mode"}
            />
            <Collapse in={untillRoomserviceModeEnabled} sx={{ width: 1 }}>
              <FormSelectTC
                defaultValue={false}
                name={"custom_data.untill_roomservice_course_id"}
                options={convertObjectToSelectOptions(untillCourseOptions)}
                tDefault={"Untill roomservice course"}
                tKey={"custom_data.untill_roomservice_course"}
              />
              <FormSelectTC
                defaultValue={[]}
                multiple={true}
                name={"custom_data.untill_roomservice_course_whitelist"}
                options={convertObjectToSelectOptions(untillCourseOptionsWithoutDefault)}
                tDefault={"Apply roomservice only to the following courses"}
                tKey={"custom_data.untill_roomservice_course_whitelist"}
              />
            </Collapse>
          </Grid>

          <Grid alignContent={"flex-start"} container item lg={6} xs={12}>
            <FormSwitchTC
              name={"untillAllowUpdateOptions"}
              tDefault={"Options: Allow updates"}
              tKey={"vestigingen.form.untill.label.untillAllowUpdateOptions"}
            />
            <FormSwitchTC
              name={"untillImportFreeOptionAsEnabled"}
              tDefault={"Options: untillImportFreeOptionAsEnabled"}
              tKey={"vestigingen.form.untill.label.untillImportFreeOptionAsEnabled"}
            />
            <FormSwitchTC
              name={"untillImportSupplementAsEnabled"}
              tDefault={"Options: untillImportSupplementAsEnabled"}
              tKey={"vestigingen.form.untill.label.untillImportSupplementAsEnabled"}
            />
            <FormSwitchTC
              name={"untillImportCondimentAsEnabled"}
              tDefault={"Options: untillImportCondimentAsEnabled"}
              tKey={"vestigingen.form.untill.label.untillImportCondimentAsEnabled"}
            />
            <FormSwitchTC
              name={"untillCreateDepartmentForSpecialArticles"}
              tDefault={"untillCreateDepartmentForSpecialArticles"}
              tKey={"vestigingen.form.untill.label.untillCreateDepartmentForSpecialArticles"}
            />
            <FormSwitchTC
              name={"untillCreateCustomer"}
              tDefault={"untillCreateCustomer"}
              tKey={"vestigingen.form.untill.label.untillCreateCustomer"}
            />
            <FormSwitchTC
              name={"untillLegacyPreordering"}
              tDefault={"untill pre ordering"}
              tKey={"vestigingen.form.untill.label.untillLegacyPreordering"}
            />
            <FormSwitchTC
              name={"useWithoutTableOverlappingMiddleware"}
              tDefault={"Don't overlap table request"}
              tKey={"vestigingen.form.overige.label.useWithoutTableOverlappingMiddleware"}
            />
            <FormSwitchTC
              name={"oChecker"}
              tDefault={"Order checker"}
              tKey={"vestigingen.form.overige.label.orderChecker"}
            />
            <FormSwitchTC
              name={"splitItems"}
              tDefault={"Split items (Send all multiple order-items as single order-items to POS API)"}
              tKey={"vestigingen.form.overige.label.splitItems"}
            />
            <FormSwitchTC
              name={"apiaccounts__untillExcludeDeliverymodeFromOrderdescr"}
              tDefault={"Exclude Deliverymode from Orderdescr"}
              tKey={"vestigingen.form.untill.label.untillExcludeDeliverymodeFromOrderdescr"}
            />
            <FormSwitchTC
              name={"apiaccounts__untillExcludeOrdernumberFromOrderdescr"}
              tDefault={"Exclude Ordernumber from Orderdescr"}
              tKey={"vestigingen.form.untill.label.untillExcludeOrdernumberFromOrderdescr"}
            />{" "}
            <FormSwitchTC
              name={"apiaccounts__untillAddCustomerToTable"}
              tDefault={"Set name to orderName"}
              tKey={"vestigingen.form.untill.label.untillAddCustomerToTable"}
            />
            <Card style={{ width: "100%" }}>
              <CardBody>
                <h4>
                  <TranslationWrapper
                    tDefault={"Api field templates (Overriding defaults values)"}
                    tKey={"vestigingen.form.untill.apiFieldTemplates"}
                  />
                </h4>
                <FormLabel>
                  <TranslationWrapper
                    tDefault={"Usable placeholder variables in templates: "}
                    tKey={"vestigingen.form.untill.placeholderVariablesInfo"}
                  />
                </FormLabel>
                <FormFieldTwoColumns>
                  <FormLabel>[name] [address] [phone] [email] [code]</FormLabel>
                </FormFieldTwoColumns>
                <FormFieldTwoColumns>
                  <FormLabel>
                    <div>[deliveryMode] [deliveryDatetime] [deliveryTime]</div>
                  </FormLabel>
                </FormFieldTwoColumns>
                <FormFieldTwoColumns>
                  <FormLabel>
                    <div> [pickupNr] [orderNumber] [jamezzOrderId] [customTableName] [notes]</div>
                  </FormLabel>
                </FormFieldTwoColumns>
                <FormFieldTwoColumns>
                  <FormLabel>
                    <div> Add custom fields using [field:customFieldName1] [field:customFieldName2] etc...</div>
                  </FormLabel>
                </FormFieldTwoColumns>
                <FormTextTC
                  name={"apiaccounts__untillTemplateExtra6"}
                  tDefault={"Template Extra6"}
                  tKey={"vestigingen.form.untill.label.untillTemplateExtra6"}
                />
                <FormTextTC
                  name={"apiaccounts__untillTemplateExtra7"}
                  tDefault={"Template Extra7"}
                  tKey={"vestigingen.form.untill.label.untillTemplateExtra7"}
                />
                <FormTextTC
                  name={"apiaccounts__untillTemplateExtra8"}
                  tDefault={"Template Extra8"}
                  tKey={"vestigingen.form.untill.label.untillTemplateExtra8"}
                />
                <FormTextTC
                  name={"apiaccounts__untillTemplateExtra9"}
                  tDefault={"Template Extra9"}
                  tKey={"vestigingen.form.untill.label.untillTemplateExtra9"}
                />
                <FormTextTC
                  name={"apiaccounts__orderDescrTemplate"}
                  tDefault={"Template Orderdescr"}
                  tKey={"vestigingen.form.untill.label.orderDescrTemplate"}
                />
                <hr />
                <FormLabel>
                  <TranslationWrapper
                    tDefault={"(Adding a leading comma is allowed. Example: Use [,email] instead of [email] )"}
                    tKey={"vestigingen.form.untill.placeholderVariablesInfofooter"}
                  />
                </FormLabel>
                <hr />
              </CardBody>
            </Card>
          </Grid>
        </Grid>

        <MenuSyncSettings />

        <Grid container spacing={1} style={{ maxWidth: 1400 }}>
          <Grid item xs={12}>
            <h3>
              <TranslationWrapper
                tDefault={"Payment IDS for using multiple payment methods"}
                tKey={"vestigingen.form.extra_settings.payments.json"}
              />
            </h3>
          </Grid>
          <Grid item xs={12}>
            <Buttonbar disableAll={showInputForm} onClick={handleButtonClick} />
          </Grid>
        </Grid>

        <Grid container spacing={1} style={{ maxWidth: 1400 }}>
          <Grid item xs={12}>
            {form.current}
          </Grid>
        </Grid>
        <Overzicht customFields={extra_settings__paymentApiIdsValue} disableAll={showInputForm} onDelete={onDelete} />
        <Grid container spacing={1} style={{ maxWidth: 1400 }}>
          <Grid item xs={12}>
            <h3>
              <TranslationWrapper tDefault={"JSON"} tKey={"vestigingen.form.customfields.headers.json"} />
            </h3>
          </Grid>
          <FormJson
            name={"extra_settings__paymentApiIds"}
            tDefault={"JSON"}
            tKey={"vestigingen.form.customfields.headers.json"}
          />
        </Grid>
      </CardBody>
    </Card>
  );
}

export default Untill;
